import React from 'react';
import { X, Folder, FolderOpen } from 'lucide-react';

interface Folder {
    id: number;
    name: string;
    media_count?: number;
}

interface MoveFolderModalProps {
    isOpen: boolean;
    onClose: () => void;
    folders: Folder[];
    currentFolderId: number | null;
    onSubmit: (folderId: number | null) => Promise<void>;
}

export const MoveFolderModal: React.FC<MoveFolderModalProps> = ({ isOpen, onClose, folders, currentFolderId, onSubmit }) => {
    const [selectedFolderId, setSelectedFolderId] = React.useState<number | null>(currentFolderId);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        setSelectedFolderId(currentFolderId);
    }, [currentFolderId]);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await onSubmit(selectedFolderId);
            onClose();
        } catch (e) {
            console.error('Erro ao mover mídia:', e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 1000, backdropFilter: 'blur(8px)'
        }}>
            <div style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                border: '1px solid #333',
                borderRadius: '16px',
                width: '90%',
                maxWidth: '450px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }}>
                <div style={{
                    padding: '24px 32px',
                    borderBottom: '1px solid #333',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#fff' }}>
                        Mover para Pasta
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'rgba(255,255,255,0.1)',
                            border: 'none',
                            borderRadius: '8px',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <X size={18} color="#fff" />
                    </button>
                </div>

                <div style={{ padding: '32px' }}>
                    <div style={{ marginBottom: '24px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#888', marginBottom: '12px' }}>
                            Selecione o destino
                        </label>

                        {/* Opção Raiz */}
                        <div
                            onClick={() => setSelectedFolderId(null)}
                            style={{
                                padding: '12px 16px',
                                background: selectedFolderId === null ? 'rgba(255, 106, 0, 0.1)' : 'rgba(255,255,255,0.05)',
                                border: selectedFolderId === null ? '1px solid #ff6a00' : '1px solid #333',
                                borderRadius: '10px',
                                marginBottom: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                transition: 'all 0.2s'
                            }}
                        >
                            <FolderOpen size={18} color={selectedFolderId === null ? '#ff6a00' : '#888'} />
                            <span style={{ color: '#fff', fontSize: '14px', fontWeight: 500 }}>
                                Todas as Mídias (Raiz)
                            </span>
                        </div>

                        {/* Pastas */}
                        {folders.map(folder => (
                            <div
                                key={folder.id}
                                onClick={() => setSelectedFolderId(folder.id)}
                                style={{
                                    padding: '12px 16px',
                                    background: selectedFolderId === folder.id ? 'rgba(255, 106, 0, 0.1)' : 'rgba(255,255,255,0.05)',
                                    border: selectedFolderId === folder.id ? '1px solid #ff6a00' : '1px solid #333',
                                    borderRadius: '10px',
                                    marginBottom: '8px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <Folder size={18} color={selectedFolderId === folder.id ? '#ff6a00' : '#888'} />
                                <span style={{ color: '#fff', fontSize: '14px', fontWeight: 500, flex: 1 }}>
                                    {folder.name}
                                </span>
                                {folder.media_count !== undefined && (
                                    <span style={{ fontSize: '12px', color: '#666' }}>
                                        ({folder.media_count})
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button
                            onClick={onClose}
                            disabled={loading}
                            style={{
                                padding: '12px 24px',
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid #333',
                                borderRadius: '8px',
                                color: '#fff',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            style={{
                                padding: '12px 24px',
                                background: loading ? '#555' : 'var(--btn-gradient)',
                                border: 'none',
                                borderRadius: '8px',
                                color: '#fff',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: loading ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {loading ? 'Movendo...' : 'Mover'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
